// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { PageHero, PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { ImageTextSection } from "@/components/ImageTextSection";
import { FeatureGrid } from "@/components/FeatureGrid";
import { ApplicationCard } from "@/components/ApplicationCard";
import { CtaSection } from "@/components/CtaSection";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Icons
import {
  Satellite,
  Orbit,
  Scale,
  Repeat,
  Leaf,
  Zap,
  Gauge,
  ShieldCheck,
  Droplets,
  Grid2x2,
  Rocket,
  Crosshair,
  RefreshCw,
  Package,
  SlidersHorizontal,
  BatteryCharging,
  Activity,
  Box,
  RotateCcw,
  Cpu,
  Shuffle,
  Network,
  Shield,
  FlaskConical,
} from "lucide-react";

// Images
import heroVideo from "@/assets/video/atlas-htp.mp4";
import application1 from "@/assets/application1.png";
import application2 from "@/assets/application2.png";
import application3 from "@/assets/application3.png";
import application4 from "@/assets/application4.png";
import atlasImage from "@/assets/atlas.png";
import pururuca from "@/assets/pururuca.png";
import ctaImg from "@/assets/cta-atlas.png";
import estagio5Video from "@/assets/video/estagio5.mp4";
import estagio1Video from "@/assets/video/estagio1.mp4";
import finepLogo from "@/assets/partners/finep.png";

export const Route = createFileRoute("/$lang/products/atlas-htp")({
  head: () => ({
    meta: [
      { title: i18n.t("products/atlas-htp:metaTitle", "ATLAS-HTP Upper Stage & OTV — BIZU Space") },
      {
        name: "description",
        content: i18n.t("products/atlas-htp:metaDesc",
          "ATLAS-HTP, an integrated green-propulsion platform combining main engines, attitude thrusters, and tanks into a complete orbital transfer vehicle (OTV).",
        ),
      },
      { property: "og:title", content: i18n.t("products/atlas-htp:metaTitle", "ATLAS-HTP Upper Stage & OTV — BIZU Space") },
      {
        property: "og:description",
        content: i18n.t("products/atlas-htp:metaOgDesc",
          "A complete non-cryogenic orbital platform with integrated main propulsion, composite tanks, and HTP attitude control.",
        ),
      },
      { property: "og:url", content: "/products/atlas-htp" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/products/atlas-htp" }],
  }),
  component: AtlasHTP,
});

function AtlasHTP() {
  const { t } = useTranslation("products/atlas-htp");

  const missionBullets = [
    {
      title: t("bullets.precision", "Precise orbital injection"),
      icon: Crosshair,
    },
    {
      title: t("bullets.longBurn", "Long-burn missions"),
      icon: Gauge,
    },
    {
      title: t("bullets.plane", "Plane changes and circularization"),
      icon: Network,
    },
    {
      title: t("bullets.phasing", "Payload redistribution and phasing"),
      icon: Shuffle,
    },
  ];

  const applications = [
    {
      title: t("apps.a1Title", "Launch Vehicle Upper Stages"),
      description: t("apps.a1Desc", "Controlled orbital injection, long-burn missions and precise maneuvers."),
      icon: Rocket,
      image: application1,
    },
    {
      title: t("apps.a2Title", "Orbital Transfer Vehicles (OTVs)"),
      description: t("apps.a2Desc", "Satellite phasing, orbit adjustment, re-orbiting and payload redistribution."),
      icon: Satellite,
      image: application2,
    },
    {
      title: t("apps.a3Title", "Responsive Space Missions"),
      description: t("apps.a3Desc",
        "Integrated liquid propulsion for missions requiring precision, flexibility and storable.",
      ),
      icon: FlaskConical,
      image: application3,
    },
    {
      title: t("apps.a4Title", "Dual-Use & Strategic Platforms"),
      description: t("apps.a4Desc", "A scalable architecture for space and defense-oriented applications."),
      icon: Shield,
      image: application4,
    },
  ];

  const otvCapabilities = [
    {
      title: t("capabilities.c1Title", "Integrated Stage Architecture"),
      description: t("capabilities.c1Desc",
        "Main propulsion, control, tank and onboard subsystems conceived as one orbital platform.",
      ),
      icon: Grid2x2,
    },
    {
      title: t("capabilities.c2Title", "ARION-Class Main Propulsion"),
      description: t("capabilities.c2Desc",
        "Regeneratively cooled HTP / Jet-A propulsion for upper-stage and transfer missions.",
      ),
      icon: Rocket,
    },
    {
      title: t("capabilities.c3Title", "Gimbal-Controlled Powered Flight"),
      description: t("capabilities.c3Desc", "Vector control authority during propulsive phases."),
      icon: Crosshair,
    },
    {
      title: t("capabilities.c4Title", "HTP Reaction Control System"),
      description: t("capabilities.c4Desc", "Pitch, yaw, roll and orbital maneuver capability beyond the atmosphere."),
      icon: RefreshCw,
    },
    {
      title: t("capabilities.c5Title", "Lightweight Orbital Tankage"),
      description: t("capabilities.c5Desc", "Mass-efficient tank architecture designed for orbital applications."),
      icon: Package,
    },
    {
      title: t("capabilities.c6Title", "Microgravity Propellant Management"),
      description: t("capabilities.c6Desc", "Stable propellant delivery under low-gravity conditions."),
      icon: SlidersHorizontal,
    },
    {
      title: t("capabilities.c7Title", "Storable, Non-Cryogenic Operation"),
      description: t("capabilities.c7Desc", "Simplified handling, storage and mission readiness."),
      icon: BatteryCharging,
    },
    {
      title: t("capabilities.c8Title", "Embedded Monitoring & Control"),
      description: t("capabilities.c8Desc", "Real-time onboard supervision and integrated operational logic."),
      icon: Cpu,
    },
  ];

  return (
    <PageShell>
      {/* HERO SECTION */}
      <PageHero
        eyebrow={t("eyebrow", "In Development")}
        title={
          <>
            ATLAS-HTP
            <br />
            UPPER STAGE & OTV
          </>
        }
        description={t("heroDesc", "An integrated liquid-propulsion platform for upper stages and orbital transfer vehicles. Based on an HTP-centered architecture, ATLAS-HTP combines propulsion, attitude control, lightweight tanks and onboard systems into a single stage designed for precise orbital missions.")}
        video={heroVideo}
      >
        <div className="mt-8">
          <a href="https://www.finep.gov.br/" target="_blank" rel="noopener noreferrer" className="inline-block transition-transform hover:scale-105">
            <img src={finepLogo} alt="Finep Logo" className="h-12 w-auto object-contain mix-blend-screen opacity-90 hover:opacity-100" />
          </a>
        </div>
      </PageHero>

      {/* SECTION 1: DESIGNED FOR CONTROLLED ORBITAL MISSIONS */}
      <ImageTextSection
        title={t("precisionTitle", "Designed for Controlled Orbital Missions")}
        subtitle={t("precisionSubtitle", "Moving in space is the next challenge for space systems.")}
        video={estagio5Video}
        imagePosition="right"
        variant="container"
        hasBorderTop={true}
        hasBorderBottom={false}
        paddingBottom="none"
        description={
          <>
            <p className="mt-5">
              {t("precisionDesc1", "Upper stages and orbital transfer vehicles must inject payloads precisely, perform correction maneuvers, adjust orbital planes, and redistribute satellites after deployment.")}
            </p>
            <p className="mt-5">
              {t("precisionDesc2", "ATLAS-HTP is being developed to meet this need — as an integrated liquid-propellant platform intended for orbital injection, long-burn missions, phasing, re-orbiting and in-space transfer operations.")}
            </p>
          </>
        }
      />

      {/* SECTION 1 BULLETS */}
      <div className="border-b border-accent bg-black">
        <div className="container-bizu py-12">
          <FeatureGrid items={missionBullets} columns={4} centered={true} />
        </div>
      </div>

      {/* SECTION 4: ENGINEERED AS A COMPLETE FLIGHT SYSTEM */}
      <section className="relative overflow-hidden py-32 text-white">
        <img
          src={atlasImage}
          alt="Orbit view"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-black/65" />

        <div className="relative container-bizu z-10">
          <Reveal effect="scale" className="mx-auto max-w-3xl text-center mb-20">
            <h2 className="display mt-5 text-4xl uppercase md:text-5xl">
              {t("systemTitle", "ENGINEERED AS A COMPLETE FLIGHT SYSTEM")}
            </h2>

            <p className="mt-6 text-lg text-white/70 leading-relaxed">
              {t("systemDesc1", "The Atlas-HTP is a fully integrated space vehicle, combining propulsion, structures, avionics, and power subsystems into a cohesive platform.")}
            </p>

            <p className="mt-6 text-lg text-white/70 leading-relaxed">
              {t("systemDesc2", "Built around our flight-proven HTP propulsion, the stage is designed for rapid assembly, simplified integration, and long-term storage readiness on the pad or in orbit.")}
            </p>
          </Reveal>

          <Reveal stagger className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              t("features.f1", "Regenerative ARION main engine"),
              t("features.f2", "Lightweight composite tanks with metallic liner"),
              t("features.f3", "Propellant Management System for microgravity"),
              t("features.f4", "Gimbaled thrust vector control"),
              t("features.f5", "HTP monopropellant RCS"),
              t("features.f6", "Hydraulic and pneumatic subsystems"),
              t("features.f7", "Real-time onboard control electronics"),
              t("features.f8", "Catalytic subsystem for controlled HTP decomposition"),
            ].map((title) => (
              <div
                key={title}
                className="flex min-h-[110px] items-center justify-center rounded-xl border border-white/10 bg-black/60 px-6 py-8 text-center backdrop-blur-md transition duration-500 hover:bg-black/80"
              >
                <h3 className="text-lg leading-relaxed text-white">{title}</h3>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* SECTION 3: WHY HTP? WHY NOW? */}
      <ImageTextSection
        title={t("whyTitle", "WHY HTP? WHY NOW?")}
        subtitle={t("whySubtitle", "ATLAS-HTP is aligned with the broader industry shift toward lower-toxicity, more operationally practical propulsion architectures.")}
        image={pururuca}
        imagePosition="left"
        hasBorderTop={true}
        description={
          <>
            <p>
              {t("whyDesc1", "Across the industry, agencies such as ESA and NASA have supported the development of alternatives to legacy toxic propulsion architectures in search of safer handling, reduced ground complexity, lower operational burden and more sustainable mission logistics.")}
            </p>
            <p className="mt-5">
              {t("whyDesc2", "By centering the platform on High Test Peroxide, ATLAS-HTP follows this direction with a storable, non-cryogenic architecture designed to simplify operations while enabling precise orbital control.")}
            </p>
            <p className="mt-5">
              {t("whyDesc3", "ESA has explicitly advanced hydrogen peroxide-based propulsion as a safer, more sustainable and lower-cost alternative to hydrazine in selected applications, while NASA has pursued green-propulsion programs aimed at reducing toxic handling concerns and accelerating launch processing.")}
            </p>
          </>
        }
      />

      {/* SECTION 4: KEY CAPABILITIES */}
      <section className="relative overflow-hidden bg-black py-28 text-white border-b border-accent">
        <div className="container-bizu">
          <Reveal effect="up" className="mx-auto max-w-3xl text-center mb-16">
            <h2 className="display mt-5 text-4xl uppercase md:text-5xl">{t("capabilitiesTitle", "KEY CAPABILITIES")}</h2>
          </Reveal>

          <FeatureGrid columns={4} centered items={otvCapabilities} />
          <Reveal effect="scale" className="mt-16">
            <div className="relative overflow-hidden rounded-3xl flex items-center justify-center">
              <video
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover opacity-90"
              >
                <source src={estagio1Video} type="video/mp4" />
              </video>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 5: APPLICATIONS */}
      <section className="relative overflow-hidden bg-black py-28 text-white">
        <div className="container-bizu">
          <Reveal effect="up" className="mb-16 text-center">
            <h2 className="display mt-5 text-4xl uppercase md:text-6xl">{t("appsTitle", "Mission Applications")}</h2>
          </Reveal>

          <Reveal stagger className="grid gap-6 md:grid-cols-2">
            {applications.map((item, idx) => (
              <ApplicationCard
                key={item.title}
                title={item.title}
                description={item.description}
                image={item.image}
                icon={item.icon}
                index={idx}
              />
            ))}
          </Reveal>
        </div>
      </section>

      <CtaSection
        image={ctaImg}
        title={t("ctaTitle", "BUILT FOR ORBIT. DESIGNED AS A PLATFORM.")}
        subtitle={t("ctaSubtitle", "A propulsion platform designed for precise control, flexible integration and reliable in-space performance.")}
        buttonText={t("ctaBtn", "Explore ARION")}
        href="/products/arion-liquid-rocket-engine"
        secondaryButtonText={t("ctaSecondaryBtn", "Learn about HTP")}
        secondaryButtonHref="/products/htp"
      />
    </PageShell>
  );
}

